/**
 * TaskDetailsContent - the canvas's body, one tab at a time.
 *
 * THE HEIGHT CONTRACT LIVES HERE. Every branch returns a `flex-1 min-h-0`
 * child of the surface's canvas, which is a real already-computed height —
 * the page used to wrap all three in ONE `h-[calc(100vh-150px)]` scroller,
 * a hard-coded guess at the frame above it that was wrong on any viewport
 * where the header wrapped.
 *
 * It is per tab rather than one wrapper because the tabs want different
 * things: Details and Attachments scroll, and the Feed is `h-full
 * overflow-hidden` with its own internal scroller, so putting it inside a
 * scrolling box collapses it. See `shared/surface/README`.
 */

import React from 'react';
import TaskDetailsTabContent from './tabs/TaskDetails';
import TaskAttachmentsTabContent from './tabs/TaskAttachments';
import { ActivityFeed } from '../shared/feed';

interface TaskDetailsContentProps {
  activeTab: string;
  taskDetails: any;
  taskUuid: string;
  userAccessLevel: 'edit' | 'view' | 'owner' | 'admin';
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  formik: any;
  hasChanges: boolean;
  setHasChanges: (changes: boolean) => void;
  onSubmit: () => void;
  taskStatus: string;
  comments: any[];
  setComments: React.Dispatch<React.SetStateAction<any[]>>;
  collaborators: any[];
  setCollaborators: React.Dispatch<React.SetStateAction<any[]>>;
  attachments: any[];
  setAttachments: React.Dispatch<React.SetStateAction<any[]>>;
  matterData: any;
  setMatterData: React.Dispatch<React.SetStateAction<any>>;
  commentsLoading: boolean;
  collaboratorsLoading: boolean;
  attachmentsLoading: boolean;
  newComment: string;
  setNewComment: React.Dispatch<React.SetStateAction<string>>;
  refetchTabData: (tab: string) => Promise<void>;
  handleAddComment: () => Promise<void>;
  handleOpenUploadModal: () => void;
  handleFileUpload?: (files: File[]) => Promise<void> | void;
  handleOpenAttachment: (attachment: any) => Promise<void>;
  handleDeleteAttachment: (attachmentId: string) => void;
  /** Opens the matter picker side panel. */
  onChangeMatter?: () => void;
  onOpenAuditLog?: () => void;
  // Feed props
  taskFeed?: any[];
  taskFeedLoading?: boolean;
  onRefreshFeed?: () => Promise<void>;
  onPostFeedEvent?: (eventData: { event_type: string; message: string; metadata?: any }) => Promise<void>;
  onFeedCommentAdd?: (eventId: string, comment: string) => Promise<void>;
  currentUser?: { id: string; first_name: string; last_name: string; email: string };
  feedMembers?: Array<{ id: string; first_name: string; last_name: string; email: string }>;
}

const TaskDetailsContent: React.FC<TaskDetailsContentProps> = ({
  activeTab,
  taskDetails,
  taskUuid,
  userAccessLevel,
  isEditing,
  setIsEditing,
  formik,
  hasChanges,
  setHasChanges,
  onSubmit,
  taskStatus,
  comments,
  setComments,
  collaborators,
  setCollaborators,
  attachments,
  setAttachments,
  matterData,
  setMatterData,
  commentsLoading,
  collaboratorsLoading,
  attachmentsLoading,
  newComment,
  setNewComment,
  refetchTabData,
  handleAddComment,
  handleOpenUploadModal,
  handleFileUpload,
  handleOpenAttachment,
  handleDeleteAttachment,
  onChangeMatter,
  onOpenAuditLog,
  taskFeed = [],
  taskFeedLoading = false,
  onRefreshFeed,
  onPostFeedEvent,
  onFeedCommentAdd,
  currentUser,
  feedMembers = [],
}) => {
  const canPost = userAccessLevel !== 'view' && !['completed', 'deleted'].includes(taskStatus);

  const renderContent = () => {
    switch (activeTab) {
      case 'Details':
        return (
          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main pt-2 pb-6">
          <TaskDetailsTabContent
            taskDetails={taskDetails}
            taskUuid={taskUuid}
            userAccessLevel={userAccessLevel}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            formik={formik}
            hasChanges={hasChanges}
            setHasChanges={setHasChanges}
            onSubmit={onSubmit}
            taskStatus={taskStatus}
            onChangeMatter={onChangeMatter}
            onOpenAuditLog={onOpenAuditLog}
          />
          </div>
        );
      case 'Feed':
        return (
          <div className="flex-1 min-h-0 flex flex-col px-4 pb-3">
            <ActivityFeed
              events={taskFeed}
              loading={taskFeedLoading}
              onRefresh={onRefreshFeed || (async () => {})}
              onPostEvent={canPost ? onPostFeedEvent : undefined}
              onCommentAdd={canPost ? onFeedCommentAdd : undefined}
              currentUser={currentUser}
              members={feedMembers}
            />
          </div>
        );
      case 'Attachments':
        // NOT a scroller: `AttachmentList` owns its own scrolling, and the
        // tab's drop target has to be the full height of the list area.
        return (
          <div className="flex-1 min-h-0 flex flex-col px-4 py-4">
          <TaskAttachmentsTabContent
            taskDetails={taskDetails}
            taskUuid={taskUuid}
            userAccessLevel={userAccessLevel}
            taskStatus={taskStatus}
            attachments={attachments}
            setAttachments={setAttachments}
            attachmentsLoading={attachmentsLoading}
            handleOpenUploadModal={handleOpenUploadModal}
            handleFileUpload={handleFileUpload}
            handleOpenAttachment={handleOpenAttachment}
            handleDeleteAttachment={handleDeleteAttachment}
          />
          </div>
        );
      default:
        return null;
    }
  };

  return renderContent();
};

export default TaskDetailsContent;
